import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { environment } from "../../../environments/environment";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  user: any = null;
  apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  private isLocalStorageAvailable(): boolean {
    return typeof window !== "undefined" && !!window.localStorage;
  }

  getAuthHeaders(options?: { skipAuth?: boolean }): HttpHeaders {
    let headers = new HttpHeaders({ "Content-Type": "application/json" });

    if (options?.skipAuth) return headers;

    const token = this.getToken();
    if (token) {
      headers = headers.set("Authorization", `Bearer ${token}`);
    }

    return headers;
  }

  getUserDetails(): Observable<any> {
    return this.http
      .get(this.apiUrl + "/auth/me", {
        headers: this.getAuthHeaders(),
        withCredentials: true,
      })
      .pipe(
        tap((user: any) => {
          this.user = user;
        }),
        catchError((err) => throwError(() => err)),
      );
  }

  getToken(): string | null {
    if (!this.isLocalStorageAvailable()) return null;
    return localStorage.getItem("access_token");
  }

  setAccessToken(access: string): void {
    if (!this.isLocalStorageAvailable()) return;
    localStorage.setItem("access_token", access);
  }

  refreshToken(): Observable<any> {
    return this.http
      .post<any>(
        `${this.apiUrl}/auth/refresh/`,
        {},
        {
          withCredentials: true,
          headers: new HttpHeaders({ "Content-Type": "application/json" }),
        },
      )
      .pipe(
        tap((response) => {
          if (response.access) {
            this.setAccessToken(response.access);
          }
        }),
      );
  }

  logout(): void {
    if (this.isLocalStorageAvailable()) {
      localStorage.removeItem("access_token");
    }

    this.user = null;

    this.http
      .post(
        `${this.apiUrl}/auth/logout/`,
        {},
        {
          withCredentials: true,
        },
      )
      .subscribe({
        next: () => {},
        error: () => {},
      });
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const expMs = payload.exp * 1000;
      return Date.now() > expMs;
    } catch {
      return true;
    }
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    return !!token && !this.isTokenExpired(token);
  }
}
